// ── SalesActivityPanel — one record's interaction history, merged ──
//
// Notes, captured email and completed follow-ups in a single date-ordered
// stream. Same prop shape as NotesPanel and EmailActivityPanel, so it drops in
// wherever either of those already sits.
//
// Why merged rather than two tabs: the follow-up system shows a last-touch date
// and schedules the next chase from it. A rep looking at "last touch 6 Aug" has
// one question — what happened on 6 Aug — and answering it by checking a Notes
// tab, then an Email tab, then remembering that a completed follow-up counts
// too, is three places to look for one answer. Here the top row IS the last
// touch.
import { useState, useEffect } from "react";
import { B } from "./theme";
import { Btn, Spinner } from "./ui";
import { useApp } from "./appContext";

// One vocabulary for the three kinds of touch, used by the chips, the filter
// and the last-touch line, so they cannot describe the same event differently.
export const SOURCE_META = {
  note:     { label: "Note",      verb: "a note",           fg: "#7a5200", bg: "#FAEEDA", br: "#EBD3A0" },
  email:    { label: "Email",     verb: "an email",         fg: "#185FA5", bg: "#E6F1FB", br: "#BFDCF5" },
  followup: { label: "Follow-Up", verb: "a completed call", fg: "#0F6E56", bg: "#E1F5EE", br: "#B6E3D3" },
};

const DIR = { inbound: "In", outbound: "Out" };

const when = (d) => d
  ? new Date(d).toLocaleString("en-US", { year: "numeric", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })
  : "—";
const dayOnly = (d) => d
  ? new Date(d).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })
  : "—";

// "3 weeks ago" reads faster than a date when the question is "has this gone
// cold" — which is the only question anyone asks of a last-touch line.
export const agoLabel = (d, now = new Date()) => {
  if (!d) return null;
  const days = Math.floor((now.getTime() - new Date(d).getTime()) / 86400000);
  if (!Number.isFinite(days)) return null;
  if (days <= 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 30) return days + " days ago";
  const months = Math.round(days / 30);
  if (months < 24) return months + (months === 1 ? " month ago" : " months ago");
  return Math.round(days / 365) + " years ago";
};

const Chip = ({ meta, children, title }) => (
  <span title={title} style={{
    fontSize: 10, fontWeight: 700, color: meta.fg, background: meta.bg,
    border: "0.5px solid " + meta.br, borderRadius: 4, padding: "1px 6px",
    textTransform: "uppercase", whiteSpace: "nowrap",
  }}>{children}</span>
);

// The one-line answer to "what was the last touch". Exported so a record header
// can show it without mounting the whole timeline.
export const LastTouchLine = ({ lasttouch }) => {
  if (!lasttouch || !lasttouch.at) {
    return <span style={{ color: "var(--color-text-secondary)" }}>No contact on record</span>;
  }
  const meta = SOURCE_META[lasttouch.source] || SOURCE_META.note;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, flexWrap: "wrap", justifyContent: "flex-end" }}>
      {dayOnly(lasttouch.at)}
      <Chip meta={meta}>{meta.label}</Chip>
      <span style={{ fontSize: 11, color: "var(--color-text-secondary)" }}>{agoLabel(lasttouch.at)}</span>
    </span>
  );
};

export const SalesActivityPanel = ({ entitytype, entityid, accountid, onLoaded }) => {
  const { api, role, currentUser } = useApp();
  const [data, setData] = useState({ rows: [], counts: {}, lasttouch: null });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [filter, setFilter] = useState("all");
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true); setErr(null);
    api.get("getSalesActivity", { entitytype, entityid })
      .then(d => {
        const next = d && d.rows ? d : { rows: [], counts: {}, lasttouch: null };
        setData(next); setLoading(false);
        if (onLoaded) onLoaded(next);
      })
      .catch(e => { setErr(e.message); setLoading(false); });
  };

  useEffect(() => {
    if (entitytype && entityid) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entitytype, entityid]);

  // Adding a note here writes an ordinary Note, which is what the last-touch
  // view already counts — so logging a call from this panel resets the cadence
  // and the follow-up engine stops chasing. That is the whole point: the place
  // you read the history is the place you add to it.
  const addNote = () => {
    if (!text.trim() || saving) return;
    setSaving(true); setErr(null);
    api.post("saveNote", {
      entitytype, entityid, accountid: accountid || null,
      source: "Sales", note: text.trim(),
      notedate: new Date().toISOString().slice(0, 10),
      createdby: (currentUser && (currentUser.name || currentUser.email)) || null,
    }).then(() => { setText(""); setSaving(false); load(); })
      .catch(e => { setErr(e.message); setSaving(false); });
  };

  const rows = data.rows || [];
  const counts = data.counts || {};
  const shown = filter === "all" ? rows : rows.filter(x => x.source === filter);

  const inputStyle = {
    width: "100%", padding: "8px 10px", fontSize: 13,
    border: "1px solid var(--color-border-secondary)", borderRadius: 6,
    resize: "vertical", boxSizing: "border-box",
    background: "var(--color-background-primary)", color: "var(--color-text-primary)",
  };

  const FilterBtn = ({ id, label, n }) => (
    <button onClick={() => setFilter(id)} style={{
      cursor: "pointer", fontSize: 12, fontWeight: 600, padding: "4px 10px", borderRadius: 999,
      border: "0.5px solid " + (filter === id ? B.cyan : "var(--color-border-tertiary)"),
      background: filter === id ? B.cyanLight : "transparent",
      color: filter === id ? B.cyanDark : "var(--color-text-secondary)",
    }}>{label}{n ? " " + n : ""}</button>
  );

  return (
    <div>
      {/* The date the follow-up engine actually used, with the reason next to
          it. Sits above the stream so the top row explains the headline. */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
        flexWrap: "wrap", padding: "8px 10px", marginBottom: 12, borderRadius: 8,
        background: "var(--color-background-secondary)",
        border: "0.5px solid var(--color-border-tertiary)",
      }}>
        <span style={{ fontSize: 12, color: "var(--color-text-secondary)", fontWeight: 600 }}>Last touch</span>
        <span style={{ fontSize: 13 }}><LastTouchLine lasttouch={data.lasttouch} /></span>
      </div>

      {role.canEdit && entityid && (
        <div style={{ marginBottom: 12 }}>
          <textarea value={text} onChange={e => setText(e.target.value)} rows={3}
            placeholder="Log a call, meeting or anything else worth remembering…" style={inputStyle} />
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 6, gap: 8 }}>
            <span style={{ fontSize: 11, color: "var(--color-text-secondary)" }}>
              Logging this counts as contact — any follow-up cadence on this record restarts from today.
            </span>
            <Btn variant="primary" disabled={saving || !text.trim()} onClick={addNote}>
              {saving ? "Saving…" : "Log Contact"}
            </Btn>
          </div>
        </div>
      )}

      {err && <div style={{ color: "#993C1D", fontSize: 12, marginBottom: 8 }}>{err}</div>}

      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 10 }}>
        <FilterBtn id="all" label="All" n={rows.length} />
        <FilterBtn id="note" label="Notes" n={counts.note} />
        <FilterBtn id="email" label="Email" n={counts.email} />
        <FilterBtn id="followup" label="Follow-Ups" n={counts.followup} />
      </div>

      {/* Said plainly rather than hidden: a timeline missing a whole category is
          worse than no timeline, because it looks complete. */}
      {data.emailIncluded === false && (
        <div style={{ fontSize: 12, color: "#7a5200", background: "#FAEEDA", border: "0.5px solid #EBD3A0",
                      borderRadius: 8, padding: "8px 12px", marginBottom: 10 }}>
          Captured email is not shown on your access level — this history covers notes and follow-ups only.
        </div>
      )}
      {data.truncated && (
        <div style={{ fontSize: 12, color: "var(--color-text-secondary)", marginBottom: 8 }}>
          Showing the most recent {rows.length}. Older activity is not listed.
        </div>
      )}

      {loading ? <Spinner />
        : shown.length === 0
          ? <div style={{ fontSize: 13, color: "var(--color-text-secondary)", padding: "8px 2px" }}>
              {rows.length ? "Nothing of that kind." : "No contact recorded yet — no notes, no captured email, no completed follow-ups."}
            </div>
          : <div style={{ maxHeight: 520, overflowY: "auto" }}>
              {shown.map(x => {
                const meta = SOURCE_META[x.source] || SOURCE_META.note;
                return (
                  <div key={x.source + ":" + x.refid} style={{ padding: "10px 2px", borderBottom: "0.5px solid var(--color-border-tertiary)" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 3 }}>
                      <Chip meta={meta}>{meta.label}</Chip>
                      {x.direction && <Chip meta={meta} title="Direction">{DIR[x.direction] || x.direction}</Chip>}
                      <span style={{ fontSize: 11, color: "var(--color-text-secondary)" }}>
                        {when(x.occurredat)}{x.actor ? " · " + x.actor : ""}
                      </span>
                      {/* A note logged today about last week's call: show the date
                          it happened, but order by when it was written — that is
                          the instant the cadence counts, and a timeline that
                          sorts by one date while displaying another is a puzzle. */}
                      {x.source === "note" && x.occurreddate &&
                        new Date(x.occurreddate).toDateString() !== new Date(x.occurredat).toDateString() && (
                        <span style={{ fontSize: 11, color: "var(--color-text-secondary)" }}>
                          (dated {dayOnly(x.occurreddate)})
                        </span>
                      )}
                      {x.hasattachments ? <span style={{ fontSize: 11, color: "var(--color-text-secondary)" }}>📎</span> : null}
                      {x.weblink && (
                        <a href={x.weblink} target="_blank" rel="noreferrer"
                           style={{ fontSize: 11, marginLeft: "auto", color: B.cyanDark }}>Open in Outlook</a>
                      )}
                    </div>
                    {x.title && <div style={{ fontSize: 13, fontWeight: 600 }}>{x.title}</div>}
                    {x.body && (
                      <div style={{ fontSize: 13, whiteSpace: "pre-wrap", color: x.source === "email" ? "var(--color-text-secondary)" : "inherit" }}>
                        {x.body}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>}
    </div>
  );
};
